import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { default as config } from "common/config/config";
import { getToken } from "./tokenProvider";
import { useAuthStore } from "features/auth/store/authStore";
import { authService } from "features/auth/services/authService";
import DomainError from "domain/error/DomainError";
import AuthErrorCodes from "domain/auth/AuthErrors";

export const apiClient: AxiosInstance = axios.create({
    baseURL: config.backendUrl,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

apiClient.interceptors.request.use(
    async (req: InternalAxiosRequestConfig) => {
        const token = await getToken();
        if (token) {
            req.headers.Authorization = `Bearer ${token}`;
        }

        // Inactivity TTL: equivalent to legacy `@Authenticated` decorator.
        // We treat any API call as "activity" and update `lastActivityDate`.
        if (useAuthStore.getState().isLoggedIn) {
            await authService.verifyInactivityTime();

            // If inactivity expired, `verifyInactivityTime` performs logout.
            // Stop the current request by throwing a DomainError consistent with the rest of the auth flow.
            if (!useAuthStore.getState().isLoggedIn) {
                throw new DomainError(AuthErrorCodes.SESSION_EXPIRED, "warning");
            }
        }

        const selectedRole = useAuthStore.getState().selectedRole;
        if (selectedRole?.roleType) {
            req.headers["authorization-role"] = selectedRole.roleType;
        }
        if (selectedRole?.resourceAddress) {
            req.headers["authorization-resource"] = selectedRole.resourceAddress;
        }

        return req;
    },
    (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { handleResponseError } = await import("./interceptors/errorInterceptor");
        return handleResponseError(error);
    },
);
